import {
  collection,
  doc,
  setDoc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  increment
} from 'firebase/firestore';
import { db } from '../firebase';
import { validateText, validateComment } from './validationService';
import { handleError, withErrorHandling } from './errorHandlingService';

// ============ USERS COLLECTION ============
export const createUserProfile = async (userId, userData) => {
  try {
    await setDoc(doc(db, 'users', userId), {
      ...userData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error('Error creating user profile:', error);
    throw error;
  }
};

export const getUserProfile = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists()) {
      return { id: userDoc.id, ...userDoc.data() };
    } else {
      return null;
    }
  } catch (error) {
    console.error('Error getting user profile:', error);
    throw error;
  }
};

export const updateUserProfile = async (userId, updates) => {
  try {
    await updateDoc(doc(db, 'users', userId), {
      ...updates,
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
};

export const getAllUsers = async (limitCount = 50) => {
  try {
    console.log('🔍 getAllUsers: Starting to fetch users...');

    // First try with orderBy createdAt
    try {
      const usersQuery = query(
        collection(db, 'users'),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );

      const querySnapshot = await getDocs(usersQuery);
      const users = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      console.log('🔍 getAllUsers: Successfully fetched users with orderBy:', users.length);
      return users;
    } catch (orderError) {
      console.warn('🔍 getAllUsers: orderBy failed, trying without orderBy:', orderError);

      // Fallback: try without orderBy
      const usersQuery = query(
        collection(db, 'users'),
        limit(limitCount)
      );

      const querySnapshot = await getDocs(usersQuery);
      const users = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      console.log('🔍 getAllUsers: Successfully fetched users without orderBy:', users.length);
      return users;
    }
  } catch (error) {
    console.error('❌ getAllUsers: Error getting users:', error);
    throw error;
  }
};

// Get user count for debugging
export const getUserCount = async () => {
  try {
    console.log('🔍 getUserCount: Checking total users...');
    const usersRef = collection(db, 'users');
    const snapshot = await getDocs(usersRef);
    const count = snapshot.size;
    console.log('🔍 getUserCount: Total users in database:', count);
    return count;
  } catch (error) {
    console.error('❌ getUserCount: Error:', error);
    return 0;
  }
};

// ============ MEMBERSHIPS COLLECTION ============
export const createMembership = async (userId, membershipData) => {
  try {
    await setDoc(doc(db, 'memberships', userId), {
      ...membershipData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error('Error creating membership:', error);
    throw error;
  }
};

export const getMembership = async (userId) => {
  try {
    const membershipDoc = await getDoc(doc(db, 'memberships', userId));
    if (membershipDoc.exists()) {
      return { id: membershipDoc.id, ...membershipDoc.data() };
    } else {
      return null;
    }
  } catch (error) {
    console.error('Error getting membership:', error);
    throw error;
  }
};

export const updateMembership = async (userId, updates) => {
  try {
    await updateDoc(doc(db, 'memberships', userId), {
      ...updates,
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error('Error updating membership:', error);
    throw error;
  }
};

// ============ EVENTS COLLECTION ============
export const createEvent = async (eventData) => {
  try {
    const eventRef = await addDoc(collection(db, 'events'), {
      ...eventData,
      attendees: [],
      registrations: [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return eventRef.id;
  } catch (error) {
    console.error('Error creating event:', error);
    throw error;
  }
};

export const getEvents = async (limitCount = 10, upcoming = true) => {
  try {
    let eventsQuery;

    if (upcoming) {
      // Try with upcoming events first
      eventsQuery = query(
        collection(db, 'events'),
        where('startDate', '>=', new Date()),
        orderBy('startDate', 'asc'),
        limit(limitCount)
      );
    } else {
      eventsQuery = query(
        collection(db, 'events'),
        orderBy('startDate', 'desc'),
        limit(limitCount)
      );
    }

    let querySnapshot = await getDocs(eventsQuery);
    let events = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // If no upcoming events found, get all events for dashboard
    if (upcoming && events.length === 0) {
      console.log('No upcoming events found, fetching all events...');
      eventsQuery = query(
        collection(db, 'events'),
        orderBy('startDate', 'desc'),
        limit(limitCount)
      );
      querySnapshot = await getDocs(eventsQuery);
      events = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    }

    console.log(`Found ${events.length} events (upcoming: ${upcoming})`);
    return events;
  } catch (error) {
    console.error('Error getting events:', error);
    // Return empty array instead of throwing to prevent dashboard from breaking
    return [];
  }
};

export const getEventById = async (eventId) => {
  try {
    const eventDoc = await getDoc(doc(db, 'events', eventId));
    if (eventDoc.exists()) {
      return { id: eventDoc.id, ...eventDoc.data() };
    } else {
      return null;
    }
  } catch (error) {
    console.error('Error getting event:', error);
    throw error;
  }
};

export const updateEvent = async (eventId, updates) => {
  try {
    await updateDoc(doc(db, 'events', eventId), {
      ...updates,
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error('Error updating event:', error);
    throw error;
  }
};

export const registerForEvent = async (eventId, userId, registrationData) => {
  try {
    await updateDoc(doc(db, 'events', eventId), {
      registrations: arrayUnion({
        userId,
        ...registrationData,
        registeredAt: new Date().toISOString()
      }),
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error('Error registering for event:', error);
    throw error;
  }
};

// Create event registration record
export const createEventRegistration = async (registrationData) => {
  try {
    const registrationRef = await addDoc(collection(db, 'eventRegistrations'), {
      ...registrationData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return registrationRef.id;
  } catch (error) {
    console.error('Error creating event registration:', error);
    throw error;
  }
};

// Update event registration
export const updateEventRegistration = async (registrationId, updates) => {
  try {
    await updateDoc(doc(db, 'eventRegistrations', registrationId), {
      ...updates,
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error('Error updating event registration:', error);
    throw error;
  }
};

// ============ EVENT COMMENTS COLLECTION ============
export const addEventComment = async (eventId, commentData) => {
  try {
    // Validate comment content
    const content = commentData.text || commentData.content;
    if (content) {
      const contentValidation = validateComment(content);
      if (!contentValidation.isValid) {
        throw new Error(contentValidation.error);
      }
      commentData.text = contentValidation.value;
      commentData.content = contentValidation.value;
    }

    const commentRef = await addDoc(collection(db, 'eventComments'), {
      ...commentData,
      eventId,
      likes: [],
      likeCount: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    // Update event comment count
    await updateDoc(doc(db, 'events', eventId), {
      commentCount: increment(1),
      updatedAt: serverTimestamp()
    });

    return commentRef.id;
  } catch (error) {
    console.error('Error adding event comment:', error);
    throw error;
  }
};

export const getEventComments = async (eventId) => {
  try {
    const q = query(
      collection(db, 'eventComments'),
      where('eventId', '==', eventId),
      orderBy('createdAt', 'asc')
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting event comments:', error);
    throw error;
  }
};

export const updateEventComment = async (commentId, updates) => {
  try {
    // Validate comment content if being updated
    if (updates.text || updates.content) {
      const content = updates.text || updates.content;
      const contentValidation = validateComment(content);
      if (!contentValidation.isValid) {
        throw new Error(contentValidation.error);
      }
      updates.text = contentValidation.value;
      updates.content = contentValidation.value;
    }

    await updateDoc(doc(db, 'eventComments', commentId), {
      ...updates,
      updatedAt: serverTimestamp()
    });

    return true;
  } catch (error) {
    console.error('Error updating event comment:', error);
    throw error;
  }
};

export const deleteEventComment = async (commentId, eventId) => {
  try {
    await deleteDoc(doc(db, 'eventComments', commentId));

    // Update event comment count
    await updateDoc(doc(db, 'events', eventId), {
      commentCount: increment(-1),
      updatedAt: serverTimestamp()
    });

    return true;
  } catch (error) {
    console.error('Error deleting event comment:', error);
    throw error;
  }
};

export const toggleEventCommentLike = async (commentId, userId) => {
  try {
    const commentRef = doc(db, 'eventComments', commentId);
    const commentDoc = await getDoc(commentRef);

    if (commentDoc.exists()) {
      const commentData = commentDoc.data();
      const likes = commentData.likes || [];

      if (likes.includes(userId)) {
        // Unlike
        await updateDoc(commentRef, {
          likes: arrayRemove(userId),
          likeCount: increment(-1),
          updatedAt: serverTimestamp()
        });
        return false; // unliked
      } else {
        // Like
        await updateDoc(commentRef, {
          likes: arrayUnion(userId),
          likeCount: increment(1),
          updatedAt: serverTimestamp()
        });
        return true; // liked
      }
    }
  } catch (error) {
    console.error('Error toggling event comment like:', error);
    throw error;
  }
};

export const deleteEvent = async (eventId) => {
  try {
    // Delete the event document
    await deleteDoc(doc(db, 'events', eventId));

    // Also delete all related event registrations
    const registrationsQuery = query(
      collection(db, 'eventRegistrations'),
      where('eventId', '==', eventId)
    );
    const registrationsSnapshot = await getDocs(registrationsQuery);

    const deletePromises = registrationsSnapshot.docs.map(doc => deleteDoc(doc.ref));
    await Promise.all(deletePromises);

    return true;
  } catch (error) {
    console.error('Error deleting event:', error);
    throw error;
  }
};

// ============ NEWS/ARTICLES COLLECTION ============
export const createArticle = async (articleData) => {
  try {
    const articleRef = await addDoc(collection(db, 'articles'), {
      ...articleData,
      views: 0,
      likes: 0,
      comments: [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return articleRef.id;
  } catch (error) {
    console.error('Error creating article:', error);
    throw error;
  }
};

export const getArticles = async (limitCount = 10, category = null, adminView = false) => {
  try {
    let articlesQuery;

    if (adminView) {
      // For admin view, get all articles regardless of status
      if (category && category !== 'all') {
        articlesQuery = query(
          collection(db, 'articles'),
          where('category', '==', category),
          limit(limitCount)
        );
      } else {
        articlesQuery = query(
          collection(db, 'articles'),
          limit(limitCount)
        );
      }
    } else {
      // For public view, only get published articles
      if (category && category !== 'all') {
        articlesQuery = query(
          collection(db, 'articles'),
          where('category', '==', category),
          where('status', '==', 'published'),
          orderBy('publishedAt', 'desc'),
          limit(limitCount)
        );
      } else {
        articlesQuery = query(
          collection(db, 'articles'),
          where('status', '==', 'published'),
          orderBy('publishedAt', 'desc'),
          limit(limitCount)
        );
      }
    }

    let querySnapshot = await getDocs(articlesQuery);
    let articles = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // If no articles found with status filter, try without it (for backward compatibility)
    if (!adminView && articles.length === 0) {
      console.log('No published articles found, trying without status filter...');
      if (category && category !== 'all') {
        articlesQuery = query(
          collection(db, 'articles'),
          where('category', '==', category),
          limit(limitCount)
        );
      } else {
        articlesQuery = query(
          collection(db, 'articles'),
          limit(limitCount)
        );
      }

      querySnapshot = await getDocs(articlesQuery);
      articles = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    }

    console.log(`Found ${articles.length} articles (category: ${category}, adminView: ${adminView})`);
    return articles;
  } catch (error) {
    console.error('Error getting articles:', error);
    // Return empty array instead of throwing to prevent UI from breaking
    return [];
  }
};

export const getArticleById = async (articleId) => {
  try {
    const articleDoc = await getDoc(doc(db, 'articles', articleId));
    if (articleDoc.exists()) {
      // Increment view count
      await updateDoc(doc(db, 'articles', articleId), {
        views: increment(1)
      });

      return { id: articleDoc.id, ...articleDoc.data() };
    } else {
      return null;
    }
  } catch (error) {
    console.error('Error getting article:', error);
    throw error;
  }
};

export const getFeaturedArticles = async (limitCount = 3) => {
  try {
    const articlesQuery = query(
      collection(db, 'articles'),
      where('featured', '==', true),
      where('status', '==', 'published'),
      orderBy('publishedAt', 'desc'),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(articlesQuery);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting featured articles:', error);
    throw error;
  }
};

export const updateArticle = async (articleId, updateData) => {
  try {
    await updateDoc(doc(db, 'articles', articleId), {
      ...updateData,
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error('Error updating article:', error);
    throw error;
  }
};

export const deleteArticle = async (articleId) => {
  try {
    // Delete all comments for this article first
    const commentsQuery = query(
      collection(db, 'articleComments'),
      where('articleId', '==', articleId)
    );
    const commentsSnapshot = await getDocs(commentsQuery);

    const deletePromises = commentsSnapshot.docs.map(doc => deleteDoc(doc.ref));
    await Promise.all(deletePromises);

    // Delete the article
    await deleteDoc(doc(db, 'articles', articleId));
    return true;
  } catch (error) {
    console.error('Error deleting article:', error);
    throw error;
  }
};

// ============ ARTICLE COMMENTS COLLECTION ============
export const addArticleComment = async (articleId, commentData) => {
  try {
    // Validate comment content
    const contentValidation = validateComment(commentData.content);
    if (!contentValidation.isValid) {
      throw new Error(contentValidation.error);
    }

    const commentRef = await addDoc(collection(db, 'articleComments'), {
      ...commentData,
      content: contentValidation.value,
      articleId,
      likes: [],
      likeCount: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    // Update article comment count
    await updateDoc(doc(db, 'articles', articleId), {
      commentCount: increment(1),
      updatedAt: serverTimestamp()
    });

    return commentRef.id;
  } catch (error) {
    console.error('Error adding article comment:', error);
    throw error;
  }
};

export const getArticleComments = async (articleId) => {
  try {
    const q = query(
      collection(db, 'articleComments'),
      where('articleId', '==', articleId),
      orderBy('createdAt', 'asc')
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting article comments:', error);
    throw error;
  }
};

export const updateArticleComment = async (commentId, updates) => {
  try {
    // Validate comment content if being updated
    if (updates.content) {
      const contentValidation = validateComment(updates.content);
      if (!contentValidation.isValid) {
        throw new Error(contentValidation.error);
      }
      updates.content = contentValidation.value;
    }

    await updateDoc(doc(db, 'articleComments', commentId), {
      ...updates,
      updatedAt: serverTimestamp()
    });

    return true;
  } catch (error) {
    console.error('Error updating article comment:', error);
    throw error;
  }
};

export const deleteArticleComment = async (commentId, articleId) => {
  try {
    await deleteDoc(doc(db, 'articleComments', commentId));

    // Update article comment count
    await updateDoc(doc(db, 'articles', articleId), {
      commentCount: increment(-1),
      updatedAt: serverTimestamp()
    });

    return true;
  } catch (error) {
    console.error('Error deleting article comment:', error);
    throw error;
  }
};

export const toggleArticleCommentLike = async (commentId, userId) => {
  try {
    const commentRef = doc(db, 'articleComments', commentId);
    const commentDoc = await getDoc(commentRef);

    if (commentDoc.exists()) {
      const commentData = commentDoc.data();
      const likes = commentData.likes || [];

      if (likes.includes(userId)) {
        // Unlike
        await updateDoc(commentRef, {
          likes: arrayRemove(userId),
          likeCount: increment(-1),
          updatedAt: serverTimestamp()
        });
        return false; // unliked
      } else {
        // Like
        await updateDoc(commentRef, {
          likes: arrayUnion(userId),
          likeCount: increment(1),
          updatedAt: serverTimestamp()
        });
        return true; // liked
      }
    }
  } catch (error) {
    console.error('Error toggling article comment like:', error);
    throw error;
  }
};

export const toggleArticleLike = async (articleId, userId) => {
  try {
    const articleRef = doc(db, 'articles', articleId);
    const articleDoc = await getDoc(articleRef);

    if (articleDoc.exists()) {
      const articleData = articleDoc.data();
      const likes = articleData.likes || [];

      if (likes.includes(userId)) {
        // Unlike
        await updateDoc(articleRef, {
          likes: arrayRemove(userId),
          likeCount: increment(-1),
          updatedAt: serverTimestamp()
        });
        return false; // unliked
      } else {
        // Like
        await updateDoc(articleRef, {
          likes: arrayUnion(userId),
          likeCount: increment(1),
          updatedAt: serverTimestamp()
        });
        return true; // liked
      }
    }
  } catch (error) {
    console.error('Error toggling article like:', error);
    throw error;
  }
};

// ============ JOBS COLLECTION ============
export const createJob = async (jobData, userId) => {
  try {
    const jobRef = await addDoc(collection(db, 'jobs'), {
      ...jobData,
      postedBy: userId,
      applications: [],
      views: 0,
      status: 'active',
      postedDate: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return jobRef.id;
  } catch (error) {
    console.error('Error creating job:', error);
    throw error;
  }
};

export const getJobs = async (limitCount = 10, filters = {}) => {
  try {
    let jobsQuery = query(
      collection(db, 'jobs'),
      where('status', '==', 'active'),
      orderBy('postedDate', 'desc'),
      limit(limitCount)
    );
    
    // Add filters if provided
    if (filters.location) {
      jobsQuery = query(jobsQuery, where('location', '==', filters.location));
    }
    if (filters.jobType) {
      jobsQuery = query(jobsQuery, where('jobType', '==', filters.jobType));
    }
    
    const querySnapshot = await getDocs(jobsQuery);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting jobs:', error);
    throw error;
  }
};

export const getJobById = async (jobId) => {
  try {
    const jobDoc = await getDoc(doc(db, 'jobs', jobId));
    if (jobDoc.exists()) {
      // Increment view count
      await updateDoc(doc(db, 'jobs', jobId), {
        views: increment(1)
      });
      
      return { id: jobDoc.id, ...jobDoc.data() };
    } else {
      return null;
    }
  } catch (error) {
    console.error('Error getting job:', error);
    throw error;
  }
};

export const applyForJob = async (jobId, userId, applicationData) => {
  try {
    await updateDoc(doc(db, 'jobs', jobId), {
      applications: arrayUnion({
        userId,
        ...applicationData,
        appliedAt: new Date().toISOString(),
        status: 'pending'
      }),
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error('Error applying for job:', error);
    throw error;
  }
};

export const updateJob = async (jobId, updates) => {
  try {
    await updateDoc(doc(db, 'jobs', jobId), {
      ...updates,
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error('Error updating job:', error);
    throw error;
  }
};

export const closeJob = async (jobId, userId) => {
  try {
    await updateDoc(doc(db, 'jobs', jobId), {
      status: 'closed',
      closedAt: serverTimestamp(),
      closedBy: userId,
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error('Error closing job:', error);
    throw error;
  }
};

export const archiveJob = async (jobId, userId) => {
  try {
    await updateDoc(doc(db, 'jobs', jobId), {
      status: 'archived',
      archivedAt: serverTimestamp(),
      archivedBy: userId,
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error('Error archiving job:', error);
    throw error;
  }
};

// Admin function to get all jobs regardless of status
export const getAllJobs = async (limitCount = 100) => {
  try {
    const jobsQuery = query(
      collection(db, 'jobs'),
      orderBy('postedDate', 'desc'),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(jobsQuery);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting all jobs:', error);
    // Return empty array instead of throwing error
    return [];
  }
};

export const deleteJob = async (jobId) => {
  try {
    // Delete all applications for this job first
    const applicationsQuery = query(
      collection(db, 'applications'),
      where('jobId', '==', jobId)
    );
    const applicationsSnapshot = await getDocs(applicationsQuery);

    const deleteApplicationPromises = applicationsSnapshot.docs.map(doc => deleteDoc(doc.ref));
    await Promise.all(deleteApplicationPromises);

    // Then delete the job itself
    await deleteDoc(doc(db, 'jobs', jobId));
    return true;
  } catch (error) {
    console.error('Error deleting job:', error);
    throw error;
  }
};

// ============ PROJECTS COLLECTION ============
export const createProject = async (projectData, userId) => {
  try {
    const projectRef = await addDoc(collection(db, 'projects'), {
      ...projectData,
      postedBy: userId,
      applications: [],
      views: 0,
      status: 'active',
      postedDate: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return projectRef.id;
  } catch (error) {
    console.error('Error creating project:', error);
    throw error;
  }
};

export const getProjects = async (limitCount = 10, filters = {}) => {
  try {
    let projectsQuery = query(
      collection(db, 'projects'),
      where('status', '==', 'active'),
      orderBy('postedDate', 'desc'),
      limit(limitCount)
    );
    
    // Add filters if provided
    if (filters.budget) {
      projectsQuery = query(projectsQuery, where('budgetRange', '==', filters.budget));
    }
    if (filters.timeline) {
      projectsQuery = query(projectsQuery, where('timeline', '==', filters.timeline));
    }
    
    const querySnapshot = await getDocs(projectsQuery);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting projects:', error);
    throw error;
  }
};

export const getProjectById = async (projectId) => {
  try {
    const projectDoc = await getDoc(doc(db, 'projects', projectId));
    if (projectDoc.exists()) {
      // Increment view count
      await updateDoc(doc(db, 'projects', projectId), {
        views: increment(1)
      });
      
      return { id: projectDoc.id, ...projectDoc.data() };
    } else {
      return null;
    }
  } catch (error) {
    console.error('Error getting project:', error);
    throw error;
  }
};

export const applyForProject = async (projectId, userId, applicationData) => {
  try {
    await updateDoc(doc(db, 'projects', projectId), {
      applications: arrayUnion({
        userId,
        ...applicationData,
        appliedAt: new Date().toISOString(),
        status: 'pending'
      }),
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error('Error applying for project:', error);
    throw error;
  }
};

export const updateProject = async (projectId, updates) => {
  try {
    await updateDoc(doc(db, 'projects', projectId), {
      ...updates,
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error('Error updating project:', error);
    throw error;
  }
};

export const closeProject = async (projectId, userId) => {
  try {
    await updateDoc(doc(db, 'projects', projectId), {
      status: 'closed',
      closedAt: serverTimestamp(),
      closedBy: userId,
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error('Error closing project:', error);
    throw error;
  }
};

export const archiveProject = async (projectId, userId) => {
  try {
    await updateDoc(doc(db, 'projects', projectId), {
      status: 'archived',
      archivedAt: serverTimestamp(),
      archivedBy: userId,
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error('Error archiving project:', error);
    throw error;
  }
};

// Admin function to get all projects regardless of status
export const getAllProjects = async (limitCount = 100) => {
  try {
    const projectsQuery = query(
      collection(db, 'projects'),
      orderBy('postedDate', 'desc'),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(projectsQuery);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting all projects:', error);
    // Return empty array instead of throwing error
    return [];
  }
};

export const deleteProject = async (projectId) => {
  try {
    // Delete all applications for this project first
    const applicationsQuery = query(
      collection(db, 'applications'),
      where('projectId', '==', projectId)
    );
    const applicationsSnapshot = await getDocs(applicationsQuery);

    const deleteApplicationPromises = applicationsSnapshot.docs.map(doc => deleteDoc(doc.ref));
    await Promise.all(deleteApplicationPromises);

    // Then delete the project itself
    await deleteDoc(doc(db, 'projects', projectId));
    return true;
  } catch (error) {
    console.error('Error deleting project:', error);
    throw error;
  }
};

// ============ FORUMS COLLECTION ============
export const createForumPost = async (postData, userId) => {
  try {
    const postRef = await addDoc(collection(db, 'forumPosts'), {
      ...postData,
      authorId: userId,
      replies: [],
      likes: [],
      views: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return postRef.id;
  } catch (error) {
    console.error('Error creating forum post:', error);
    throw error;
  }
};

export const getForumPosts = async (limitCount = 20, category = null) => {
  try {
    let postsQuery;
    
    if (category) {
      postsQuery = query(
        collection(db, 'forumPosts'),
        where('category', '==', category),
        orderBy('updatedAt', 'desc'),
        limit(limitCount)
      );
    } else {
      postsQuery = query(
        collection(db, 'forumPosts'),
        orderBy('updatedAt', 'desc'),
        limit(limitCount)
      );
    }
    
    const querySnapshot = await getDocs(postsQuery);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting forum posts:', error);
    throw error;
  }
};

export const addForumReply = async (postId, userId, replyData) => {
  try {
    await updateDoc(doc(db, 'forumPosts', postId), {
      replies: arrayUnion({
        id: Date.now().toString(),
        authorId: userId,
        ...replyData,
        createdAt: new Date().toISOString()
      }),
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error('Error adding forum reply:', error);
    throw error;
  }
};

export const likeForumPost = async (postId, userId) => {
  try {
    await updateDoc(doc(db, 'forumPosts', postId), {
      likes: arrayUnion(userId),
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error('Error liking forum post:', error);
    throw error;
  }
};

export const unlikeForumPost = async (postId, userId) => {
  try {
    await updateDoc(doc(db, 'forumPosts', postId), {
      likes: arrayRemove(userId),
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error('Error unliking forum post:', error);
    throw error;
  }
};

export const deleteForumPost = async (postId) => {
  try {
    // First, delete all comments/replies for this post
    const repliesQuery = query(
      collection(db, 'forumReplies'),
      where('postId', '==', postId)
    );
    const repliesSnapshot = await getDocs(repliesQuery);

    const deleteReplyPromises = repliesSnapshot.docs.map(doc => deleteDoc(doc.ref));
    await Promise.all(deleteReplyPromises);

    // Then delete the post itself
    await deleteDoc(doc(db, 'forumPosts', postId));
    return true;
  } catch (error) {
    console.error('Error deleting forum post:', error);
    throw error;
  }
};

// ============ GROUPS COLLECTION ============
export const createGroup = async (groupData, userId) => {
  try {
    const groupRef = await addDoc(collection(db, 'groups'), {
      ...groupData,
      createdBy: userId,
      members: [userId],
      memberCount: 1,
      posts: [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return groupRef.id;
  } catch (error) {
    console.error('Error creating group:', error);
    throw error;
  }
};

export const getGroups = async (limitCount = 20) => {
  try {
    // First try with ordering by memberCount
    let groupsQuery = query(
      collection(db, 'groups'),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(groupsQuery);
    const groups = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Sort by memberCount in JavaScript (more reliable than Firestore ordering)
    groups.sort((a, b) => (b.memberCount || 0) - (a.memberCount || 0));

    return groups;
  } catch (error) {
    console.error('Error getting groups:', error);
    throw error;
  }
};

// Admin version to get all groups
export const getAllGroups = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, 'groups'));
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting all groups:', error);
    throw error;
  }
};

export const updateGroup = async (groupId, updates) => {
  try {
    await updateDoc(doc(db, 'groups', groupId), {
      ...updates,
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error('Error updating group:', error);
    throw error;
  }
};

export const deleteGroup = async (groupId) => {
  try {
    await deleteDoc(doc(db, 'groups', groupId));
    return true;
  } catch (error) {
    console.error('Error deleting group:', error);
    throw error;
  }
};

export const getGroupMembers = async (groupId) => {
  try {
    // First get the group to see who the members are
    const groupDoc = await getDoc(doc(db, 'groups', groupId));
    if (!groupDoc.exists()) {
      return [];
    }

    const group = groupDoc.data();
    const memberIds = group.members || [];

    if (memberIds.length === 0) {
      return [];
    }

    // Get user details for each member
    const memberPromises = memberIds.map(async (userId) => {
      try {
        const userDoc = await getDoc(doc(db, 'users', userId));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          return {
            userId: userId,
            name: `${userData.firstName || ''} ${userData.lastName || ''}`.trim(),
            email: userData.email,
            joinedAt: userData.createdAt,
            role: userData.role || 'member'
          };
        }
        return null;
      } catch (error) {
        console.error(`Error getting user ${userId}:`, error);
        return null;
      }
    });

    const members = await Promise.all(memberPromises);
    return members.filter(member => member !== null);
  } catch (error) {
    console.error('Error getting group members:', error);
    throw error;
  }
};

export const getGroupById = async (groupId) => {
  try {
    console.log('getGroupById called with ID:', groupId);
    const groupDoc = await getDoc(doc(db, 'groups', groupId));
    console.log('Group document exists?', groupDoc.exists());
    if (groupDoc.exists()) {
      const groupData = { id: groupDoc.id, ...groupDoc.data() };
      console.log('Group data found:', groupData);
      return groupData;
    } else {
      console.log('No group document found for ID:', groupId);
      return null;
    }
  } catch (error) {
    console.error('Error getting group by ID:', error);
    throw error;
  }
};

export const removeUserFromGroup = async (groupId, userId) => {
  try {
    await updateDoc(doc(db, 'groups', groupId), {
      members: arrayRemove(userId),
      memberCount: increment(-1),
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error('Error removing user from group:', error);
    throw error;
  }
};

export const createGroupPost = async (postData, userId) => {
  try {
    // Validate post data
    const titleValidation = validateText(postData.title, {
      required: true,
      maxLength: 200,
      fieldName: 'Post Title'
    });
    if (!titleValidation.isValid) {
      throw new Error(titleValidation.error);
    }

    const contentValidation = validateText(postData.content, {
      required: true,
      maxLength: 5000,
      fieldName: 'Post Content'
    });
    if (!contentValidation.isValid) {
      throw new Error(contentValidation.error);
    }

    const postRef = await addDoc(collection(db, 'groupPosts'), {
      ...postData,
      title: titleValidation.value,
      content: contentValidation.value,
      authorId: userId,
      likes: [],
      likeCount: 0,
      replies: [],
      commentCount: 0,
      views: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return postRef.id;
  } catch (error) {
    console.error('Error creating group post:', error);
    throw error;
  }
};

export const getGroupPosts = async (groupId) => {
  try {
    // Simplified query without orderBy to avoid index requirement
    const q = query(
      collection(db, 'groupPosts'),
      where('groupId', '==', groupId)
    );
    const querySnapshot = await getDocs(q);
    const posts = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Sort by createdAt in JavaScript instead of Firestore
    posts.sort((a, b) => {
      const aTime = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
      const bTime = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
      return bTime - aTime; // Descending order (newest first)
    });

    return posts;
  } catch (error) {
    console.error('Error getting group posts:', error);
    throw error;
  }
};

export const deleteGroupPost = async (postId) => {
  try {
    // First, delete all comments for this post
    const commentsQuery = query(
      collection(db, 'groupComments'),
      where('postId', '==', postId)
    );
    const commentsSnapshot = await getDocs(commentsQuery);

    const deleteCommentPromises = commentsSnapshot.docs.map(doc => deleteDoc(doc.ref));
    await Promise.all(deleteCommentPromises);

    // Then delete the post itself
    await deleteDoc(doc(db, 'groupPosts', postId));
    return true;
  } catch (error) {
    console.error('Error deleting group post:', error);
    throw error;
  }
};

export const joinGroup = async (groupId, userId) => {
  try {
    await updateDoc(doc(db, 'groups', groupId), {
      members: arrayUnion(userId),
      memberCount: increment(1),
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error('Error joining group:', error);
    throw error;
  }
};

export const leaveGroup = async (groupId, userId) => {
  try {
    await updateDoc(doc(db, 'groups', groupId), {
      members: arrayRemove(userId),
      memberCount: increment(-1),
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error('Error leaving group:', error);
    throw error;
  }
};

// Add comment to group post
export const addCommentToGroupPost = async (postId, commentData) => {
  try {
    // Validate comment content
    const contentValidation = validateComment(commentData.content);
    if (!contentValidation.isValid) {
      throw new Error(contentValidation.error);
    }

    const commentRef = await addDoc(collection(db, 'groupComments'), {
      ...commentData,
      content: contentValidation.value,
      likes: [],
      likeCount: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    // Update post comment count
    await updateDoc(doc(db, 'groupPosts', postId), {
      commentCount: increment(1),
      updatedAt: serverTimestamp()
    });

    return commentRef.id;
  } catch (error) {
    console.error('Error adding comment:', error);
    throw error;
  }
};

// Get comments for a group post
export const getGroupPostComments = async (postId) => {
  try {
    console.log('Getting comments for post:', postId);
    const q = query(
      collection(db, 'groupComments'),
      where('postId', '==', postId)
    );
    const querySnapshot = await getDocs(q);
    const comments = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Sort by createdAt in JavaScript instead of Firestore
    comments.sort((a, b) => {
      const aTime = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
      const bTime = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
      return aTime - bTime; // Ascending order (oldest first)
    });

    console.log(`Found ${comments.length} comments for post ${postId}`);
    return comments;
  } catch (error) {
    console.error('Error getting comments:', error);
    throw error;
  }
};

// Like/unlike a group post
export const toggleGroupPostLike = async (postId, userId) => {
  try {
    const postRef = doc(db, 'groupPosts', postId);
    const postDoc = await getDoc(postRef);

    if (postDoc.exists()) {
      const postData = postDoc.data();
      const likes = postData.likes || [];

      if (likes.includes(userId)) {
        // Unlike
        await updateDoc(postRef, {
          likes: arrayRemove(userId),
          likeCount: increment(-1),
          updatedAt: serverTimestamp()
        });
        return false; // unliked
      } else {
        // Like
        await updateDoc(postRef, {
          likes: arrayUnion(userId),
          likeCount: increment(1),
          updatedAt: serverTimestamp()
        });
        return true; // liked
      }
    }
  } catch (error) {
    console.error('Error toggling like:', error);
    throw error;
  }
};

// Like/unlike a comment
export const toggleCommentLike = async (commentId, userId) => {
  try {
    const commentRef = doc(db, 'groupComments', commentId);
    const commentDoc = await getDoc(commentRef);

    if (commentDoc.exists()) {
      const commentData = commentDoc.data();
      const likes = commentData.likes || [];

      if (likes.includes(userId)) {
        // Unlike
        await updateDoc(commentRef, {
          likes: arrayRemove(userId),
          likeCount: increment(-1),
          updatedAt: serverTimestamp()
        });
        return false; // unliked
      } else {
        // Like
        await updateDoc(commentRef, {
          likes: arrayUnion(userId),
          likeCount: increment(1),
          updatedAt: serverTimestamp()
        });
        return true; // liked
      }
    }
  } catch (error) {
    console.error('Error toggling comment like:', error);
    throw error;
  }
};

// Update a group comment
export const updateGroupComment = async (commentId, updates) => {
  try {
    // Validate comment content if being updated
    if (updates.content) {
      const contentValidation = validateComment(updates.content);
      if (!contentValidation.isValid) {
        throw new Error(contentValidation.error);
      }
      updates.content = contentValidation.value;
    }

    await updateDoc(doc(db, 'groupComments', commentId), {
      ...updates,
      updatedAt: serverTimestamp()
    });

    return true;
  } catch (error) {
    console.error('Error updating group comment:', error);
    throw error;
  }
};

// Delete a group comment
export const deleteGroupComment = async (commentId, postId) => {
  try {
    await deleteDoc(doc(db, 'groupComments', commentId));

    // Update post comment count
    await updateDoc(doc(db, 'groupPosts', postId), {
      commentCount: increment(-1),
      updatedAt: serverTimestamp()
    });

    return true;
  } catch (error) {
    console.error('Error deleting group comment:', error);
    throw error;
  }
};



// ============ ADMIN FUNCTIONS ============
export const approveUser = async (userId) => {
  try {
    await updateDoc(doc(db, 'users', userId), {
      status: 'approved',
      approvedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error('Error approving user:', error);
    throw error;
  }
};

// Delete user from Firestore (admin only)
export const deleteUserFromFirestore = async (userId) => {
  try {
    // Delete user document
    await deleteDoc(doc(db, 'users', userId));

    // Delete membership if exists
    try {
      await deleteDoc(doc(db, 'memberships', userId));
    } catch (error) {
      // Membership might not exist, that's okay
      console.log('No membership found for user:', userId);
    }

    console.log('User deleted from Firestore:', userId);
    return true;
  } catch (error) {
    console.error('Error deleting user from Firestore:', error);
    throw error;
  }
};

// Find user by email (admin only)
export const findUserByEmail = async (email) => {
  try {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('email', '==', email));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return null;
    }

    const userDoc = querySnapshot.docs[0];
    return {
      id: userDoc.id,
      ...userDoc.data()
    };
  } catch (error) {
    console.error('Error finding user by email:', error);
    throw error;
  }
};

export const getPendingUsers = async () => {
  try {
    const usersQuery = query(
      collection(db, 'users'),
      where('status', '==', 'pending'),
      orderBy('createdAt', 'desc')
    );
    
    const querySnapshot = await getDocs(usersQuery);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting pending users:', error);
    throw error;
  }
};

// ============ ANALYTICS FUNCTIONS ============
export const getAnalytics = async () => {
  try {
    // Get total counts
    const [usersSnapshot, jobsSnapshot, projectsSnapshot, eventsSnapshot] = await Promise.all([
      getDocs(collection(db, 'users')),
      getDocs(collection(db, 'jobs')),
      getDocs(collection(db, 'projects')),
      getDocs(collection(db, 'events'))
    ]);
    
    return {
      totalUsers: usersSnapshot.size,
      totalJobs: jobsSnapshot.size,
      totalProjects: projectsSnapshot.size,
      totalEvents: eventsSnapshot.size,
      activeMembers: usersSnapshot.docs.filter(doc => doc.data().status === 'approved').length,
      pendingMembers: usersSnapshot.docs.filter(doc => doc.data().status === 'pending').length
    };
  } catch (error) {
    console.error('Error getting analytics:', error);
    throw error;
  }
};

// Fix admin user profile for testing
export const fixAdminUserProfile = async () => {
  try {
    const adminUserRef = doc(db, 'users', 'admin@the-apn.com');
    await updateDoc(adminUserRef, {
      status: 'approved',
      role: 'admin',
      isFoundingMember: true,
      isVipMember: true,
      memberSince: 'January 2020',
      chapter: 'New York',
      referralCount: 10,
      ambassadorSince: 'March 2020',
      roleStartDate: 'January 2020',
      updatedAt: serverTimestamp()
    });
    console.log('Admin user profile updated successfully');
    return true;
  } catch (error) {
    console.error('Error updating admin user profile:', error);
    return false;
  }
};
